import React from 'react';
import { Building, CalendarDays, Info, Infinity as InfinityIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatWholeDollars } from '@/shared/salesHistory';

// Three equal tiles (REQ-051), each "shown of total" and following the Status
// filter and search. GMV comes from the nightly ∑: Variants totals
// (FIG-345 · UC-230); "$shown of $all" adds up the merchants that have figures.

export interface GmvTileTotals {
  shown: number | null;
  all: number | null;
}

export interface MerchantsGmvSummary {
  state: 'loading' | 'error' | 'ready';
  allTime: GmvTileTotals;
  t12m: GmvTileTotals;
  /** Explains source and cut-off, e.g. for the ⓘ tooltip. Null when nothing is computed yet. */
  sourceNote: string | null;
}

interface MerchantsSummaryTilesProps {
  shownCount: number;
  totalCount: number;
  gmv?: MerchantsGmvSummary;
}

const Tile: React.FC<{ title: string; icon: React.ReactNode; info?: string | null; children: React.ReactNode }> = ({ title, icon, info, children }) => (
  <Card className="flex-1 basis-[280px]">
    <CardHeader className="pb-2">
      <CardTitle className="flex items-center gap-1.5 text-lg font-semibold">
        {title}
        {info && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="inline-flex text-muted-foreground" aria-label={`About ${title}: ${info}`}>
                  <Info className="h-4 w-4" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs"><p>{info}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">{children}</div>
        {icon}
      </div>
    </CardContent>
  </Card>
);

const GmvTileValue: React.FC<{ gmv?: MerchantsGmvSummary; totals?: GmvTileTotals; testId: string }> = ({ gmv, totals, testId }) => {
  if (gmv?.state === 'loading') return <span className="text-base text-muted-foreground">Loading…</span>;
  if (gmv?.state === 'error') return <span className="text-base text-muted-foreground">Couldn't load GMV</span>;
  if (!gmv?.sourceNote || !totals) return <span className="text-base text-muted-foreground">Not calculated yet</span>;
  return (
    <>
      <span className="text-3xl font-bold tabular-nums text-brand-basic" data-testid={`${testId}-shown`}>
        {totals.shown === null ? '$0' : formatWholeDollars(totals.shown)}
      </span>
      <span className="text-base font-medium tabular-nums text-muted-foreground" data-testid={`${testId}-all`}>
        of {totals.all === null ? '$0' : formatWholeDollars(totals.all)}
      </span>
    </>
  );
};

export const MerchantsSummaryTiles: React.FC<MerchantsSummaryTilesProps> = ({ shownCount, totalCount, gmv }) => (
  <div className="flex flex-1 flex-wrap gap-4" data-testid="merchants-summary-tiles">
    <Tile title="Merchants" icon={<Building className="h-8 w-8 flex-none text-brand-primary" aria-hidden="true" />}>
      <span className="text-3xl font-bold tabular-nums text-brand-basic" data-testid="merchants-shown-count">{shownCount}</span>
      <span className="text-base font-medium tabular-nums text-muted-foreground">of {totalCount}</span>
    </Tile>
    <Tile title="GMV (All Time)" info={gmv?.sourceNote} icon={<InfinityIcon className="h-8 w-8 flex-none text-brand-primary" aria-hidden="true" />}>
      <GmvTileValue gmv={gmv} totals={gmv?.allTime} testId="gmv-all-time" />
    </Tile>
    <Tile title="GMV (Trailing 12M)" info={gmv?.sourceNote} icon={<CalendarDays className="h-8 w-8 flex-none text-brand-primary" aria-hidden="true" />}>
      <GmvTileValue gmv={gmv} totals={gmv?.t12m} testId="gmv-t12m" />
    </Tile>
  </div>
);

export default MerchantsSummaryTiles;
